import Scrollspy from 'react-scrollspy-ez';
import styled from 'styled-components';

import BadgeStyled from '../../../components/Badge';
import ContentHeaderStyled from '../../../components/ContentHeader';
import ExternalLinkGroupStyled from '../../../components/ExternalLinkGroup';
import PaginationStyled from '../../../components/Pagination';
import { REFERENCE_LINKS } from '../../../constants';
import { IconDanger, IconSuccess } from '../../../core';
import commonPageContentStyles from '../../../styles/mixins/_commonPageContentStyles';
import navigationStyles from '../../../styles/mixins/navigationMenuStyles';

interface Props {
  className?: string;
}

const dataExternalLinkGroups = [
  {
    id: 1,
    title: 'Government accessibility resources',
    cards: [
      {
        title: 'The Section 508 accessibility program',
        subtitle: '[section508.gov]',
        linkUrl: REFERENCE_LINKS.section508Program,
      },
      {
        title: 'Accessibility for Teams guide',
        subtitle: '[digital.gov]',
        linkUrl: REFERENCE_LINKS.digitalGovAccessibilityForTeams,
      },
      {
        title: 'Find your Section 508 Coordinator',
        subtitle: '[section508.gov]',
        linkUrl: REFERENCE_LINKS.section508ProgramManagerListing,
      },
    ],
  },
];

const TableStructure = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="content">
      <ContentHeaderStyled
        title="Table"
        breadcrumbs={[
          { label: 'General Components', url: '/general-components' },
          { label: 'Table', url: '/' },
        ]}
      />
      <div className="page-container">
        <div className="page-content">
          <div className="page-content-with-navigation-wrapper">
            <div className="page-content-with-navigation">
              <div className="page-content-item">
                <h2 className="h1" id="overview">
                  Overview
                </h2>
                <p className="mb-40">
                  Tables are used to organize and present data in a clear, structured format,
                  enabling users to easily scan and compare information. They consist of rows and
                  columns, where each cell contains a piece of data. Tables are commonly used for
                  presenting large sets of information that require sorting, filtering, and precise
                  organization. Proper design ensures readability and accessibility, enhancing the
                  user&apos;s ability to interpret the data efficiently.
                </p>
                <h2 className="divider h1" id="specifications">
                  Specifications
                </h2>
                <h3 className="h3" id="anatomy">
                  Anatomy
                </h3>
                <p className="mb-8">The anatomy of a table consists of several key components:</p>
                <ol className="numbered-list mb-8">
                  <li>
                    Header: Contains the column titles or labels, providing context for the data
                    displayed in each column.
                  </li>
                  <li>
                    Body: The main area where data is displayed in rows and columns, allowing users
                    to compare information.
                  </li>
                  <li>
                    Rows: Horizontal sections that represent individual data entries, containing
                    values for each column.
                  </li>
                  <li>
                    Columns: Vertical sections that categorize the data, with each column
                    corresponding to a specific attribute.
                  </li>
                  <li>Cell: The intersection of a row and column, displaying the actual data.</li>
                  <li>
                    Footer: Optional, used to summarize the table or provide additional information,
                    such as totals or notes.
                  </li>
                </ol>
                <p className="mb-40">
                  These elements work together to create a functional, readable table for presenting
                  information.
                </p>
              </div>
              <img
                src="/images/general-components-table/table-1@3x.jpg"
                alt="Anatomy"
                className="mb-40 img-responsive"
              />
              <div className="page-content-item">
                <ol className="inside-list text-body14 mb-40">
                  <li className="mb-0">
                    Title: contains the data table title and an optional description.
                  </li>
                  <li className="mb-0">
                    Column header: title for the row header with the optional sorting behaviour.
                  </li>
                  <li className="mb-0">
                    Table row: can be configured to show different types of data.
                  </li>
                </ol>
              </div>
              <div className="page-content-item">
                <h3 className="h3 mb-8" id="hover">
                  Hover
                </h3>
                <p className="mb-40 text-body14">
                  Hover interactions in tables highlight rows or cells when the cursor moves over
                  them, providing visual feedback. This includes background color changes, text
                  color shifts, or tooltips, helping users navigate and interact more effectively.
                  For buttons or links, hovering may trigger color shifts or underlines to signal
                  interactivity. These effects improve accessibility and user focus.
                </p>
              </div>
              <img
                src="/images/general-components-table/table-2@3x.jpg"
                alt="Hover"
                className="mb-40 img-responsive"
              />
              <div className="page-content-item">
                <h3 className="h3 mb-8" id="sorting">
                  Sorting
                </h3>
                <p className="mb-40 text-body14">
                  Sorting in tables allows users to rearrange data by specific columns, typically
                  indicated by an up or down arrow beside the column header. Clicking the header
                  toggles between ascending and descending order. This feature improves data
                  organization and enables users to easily find information by sorting based on
                  relevance.
                </p>
              </div>
              <img
                src="/images/general-components-table/table-3@3x.jpg"
                alt="Sorting"
                className="mb-40 img-responsive"
              />
              <h2 className="h1 divider" id="guidelines">
                Guidelines
              </h2>
              <div className="page-content-item">
                <h3 className="h3" id="sizing">
                  Sizing
                </h3>
                <p className="mb-40 font-sm">
                  Sizing in tables ensures that columns are wide enough to display content clearly
                  without excessive empty space. The width of each column can be set based on the
                  content it holds, with flexible or fixed width options depending on the design.
                  This ensures readability and efficient use of space, providing a better user
                  experience.
                </p>
              </div>
              <div className="ex-cards">
                <div className="ex-card true">
                  <img
                    className="ex-card-img"
                    src="/images/general-components-table/screen-do-use-sizing@2x.webp"
                    width={488}
                    height={369}
                    alt="Do use sizing"
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="success" iconLeft={<IconSuccess />}>
                      Do
                    </BadgeStyled>
                    <p>Do use the same row height for the table and header rows.</p>
                  </div>
                </div>
                <div className="ex-card false">
                  <img
                    src="/images/general-components-table/screen-do-not-use-sizing@2x.webp"
                    width={488}
                    height={369}
                    alt="Do not use sizing"
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="danger" iconLeft={<IconDanger />}>
                      Don&apos;t
                    </BadgeStyled>
                    <p>Don&apos;t mix row heights for the table and header rows.</p>
                  </div>
                </div>
              </div>
              <div className="page-content-item">
                <h3 className="h3" id="usage">
                  Usage
                </h3>
                <p className="mb-32 text-body14">
                  In tables, ensure that each column&apos;s content is aligned and appropriately
                  sized for readability. Avoid excessive data in a single cell, as it can clutter
                  the table and make it harder for users to scan information. Utilize sorting,
                  filtering, and pagination to enhance data interaction, and ensure headers are
                  clear and concise for ease of navigation.
                </p>
                <h4 className="h4 text-h4-sb" id="when-to-use">
                  When to use
                </h4>
                <ol className="disc-list mb-32">
                  <li>To organize and display data.</li>
                  <li>
                    bIf your user must navigate to a specific piece of data to complete a task.
                  </li>
                  <li>Displaying all of a user&rsquo;s resources.</li>
                </ol>
                <h4 className="h4 text-h4-sb" id="when-not-to-use">
                  When not to use
                </h4>
                <ol className="disc-list text-body14 mb-40">
                  <li>When a more complex display o the data or interactions are required.</li>
                  <li>As a replacement for a spreadsheet application.</li>
                </ol>
              </div>
              <h2 className="h1 divider" id="references">
                References
              </h2>
              {dataExternalLinkGroups.map((externalLinkGroup) => (
                <ExternalLinkGroupStyled
                  key={externalLinkGroup.id}
                  title={externalLinkGroup.title}
                  cards={externalLinkGroup.cards}
                />
              ))}
            </div>
            <div>
              <div className="page-navigation">
                <h2>Contents</h2>
                <Scrollspy
                  ids={[
                    'overview',
                    'specifications',
                    'anatomy',
                    'hover',
                    'sorting',
                    'guidelines',
                    'sizing',
                    'usage',
                    'references',
                  ]}
                  offset={0}
                  activeItemClassName="active"
                  itemContainerClassName="multilevel-link-list"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <PaginationStyled
        previousLink="/general-components/containment"
        previous="Containment"
        nextLink="/data-visualization"
        next="Data Visualization"
      />
    </div>
  </div>
);

const Table = styled(TableStructure)`
  ${commonPageContentStyles}
  ${navigationStyles([3, 4, 5, 7, 8])}

    overflow: unset;
  height: 100%;

  > .content {
    width: 100%;
    padding: 0;
  }
`;

export default Table;
